import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const ICON_BASE_URL = 'https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/icons2'

function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const footerLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') }
  ]

  const contactInfo = {
    phone: '+1 (361) 510-6451',
    email: 'nhenaoga@gmail.com',
    city: 'San Antonio, TX'
  }

  return (
    <footer className="relative bg-gray-900 text-white overflow-hidden">
      {/* Grid Pattern - subtle on dark background */}
      <div
        className="absolute inset-0 bg-grid-dark mask-fade-top"
        aria-hidden="true"
      />

      {/* Subtle gradient glow at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-baby-blue-500/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 py-8 md:px-8 md:py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">

          {/* Company Branding Section */}
          <div className="flex flex-col space-y-4">
            <div className="bg-white rounded-lg p-3 w-fit">
              <img 
                src="https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/logo/unnamed%20(12).jpg"
                alt="Digital Amigos Logo"
                className="h-10 md:h-12 w-auto"
                crossOrigin="anonymous"
              />
            </div>
            <p className="text-sm md:text-base text-gray-300 leading-relaxed">
              {t('footer.description')}
            </p>
            <p className="text-xs md:text-sm text-gray-400 italic">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Quick Links Section */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-lg md:text-xl font-bold text-white">
              {t('footer.quickLinks')}
            </h4>
            <nav className="flex flex-col space-y-2">
              {footerLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm md:text-base text-gray-300 hover:text-baby-blue-400 transition-colors duration-200 w-fit"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Information Section */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-lg md:text-xl font-bold text-white">
              {t('footer.contactUs')}
            </h4>
            <div className="flex flex-col space-y-3">
              {/* Phone */}
              <a
                href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                className="flex items-center space-x-3 text-sm md:text-base text-gray-300 hover:text-baby-blue-400 transition-colors duration-200 group"
              >
                <img
                  src={`${ICON_BASE_URL}/phone-income-front-color.png`}
                  alt="Phone"
                  className="w-5 h-5 group-hover:opacity-80 transition-opacity"
                  crossOrigin="anonymous"
                />
                <span>{contactInfo.phone}</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center space-x-3 text-sm md:text-base text-gray-300 hover:text-baby-blue-400 transition-colors duration-200 group"
              >
                <img
                  src={`${ICON_BASE_URL}/mail-front-color.png`}
                  alt="Email"
                  className="w-5 h-5 group-hover:opacity-80 transition-opacity"
                  crossOrigin="anonymous"
                />
                <span>{contactInfo.email}</span>
              </a>

              {/* Location */}
              <div className="flex items-center space-x-3 text-sm md:text-base text-gray-300">
                <img
                  src={`${ICON_BASE_URL}/location-front-color.png`}
                  alt="Location"
                  className="w-5 h-5"
                  crossOrigin="anonymous"
                />
                <span>{contactInfo.city}</span>
              </div>
            </div>
          </div>

          {/* Business Hours Section */}
          <div className="flex flex-col space-y-4">
            <h4 className="text-lg md:text-xl font-bold text-white">
              {t('footer.businessHours')}
            </h4>
            <div className="flex flex-col space-y-2 text-sm md:text-base text-gray-300">
              <div className="flex justify-between">
                <span>{t('footer.mondayFriday')}</span>
                <span className="text-baby-blue-400 font-medium">9AM - 6PM</span>
              </div>
              <div className="flex justify-between">
                <span>{t('footer.saturday')}</span>
                <span className="text-baby-blue-400 font-medium">10AM - 4PM</span>
              </div>
              <div className="flex justify-between">
                <span>{t('footer.sunday')}</span>
                <span className="text-gray-400">{t('footer.closed')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 mb-6 md:mb-8"></div>

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          {/* Copyright */}
          <p className="text-xs md:text-sm text-gray-400 text-center md:text-left">
            &copy; {currentYear} {t('footer.copyright')}
          </p>

          {/* Social Links or Additional Info */}
          <div className="flex items-center space-x-6">
            <a
              href="/privacy-policy"
              className="text-xs md:text-sm text-gray-400 hover:text-baby-blue-400 transition-colors duration-200"
            >
              {t('footer.privacyPolicy')}
            </a>
            <a
              href="/terms-of-service"
              className="text-xs md:text-sm text-gray-400 hover:text-baby-blue-400 transition-colors duration-200"
            >
              {t('footer.termsOfService')}
            </a>
          </div>
        </div>

        {/* Bilingual Badge */}
        <div className="mt-6 md:mt-8 text-center">
          <p className="text-xs md:text-sm text-gray-400">
            {t('footer.bilingualBadge')}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
